import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import NotWiredBanner from '../../components/NotWiredBanner';
import PageHeader from '../../components/PageHeader';
import usePageTitle from '../../hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Building2,
  Plus,
  Users,
  Globe,
  Palette,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  MoreVertical,
  Trash2,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`
});

/* Tailwind needs every class spelled out (a `text-${tone}-400` template is purged), so each
 * status / plan carries its full Aurora class string: active = success, suspended = danger,
 * trial = warning; basic = muted, professional = brand primary, enterprise = violet accent. */
const STATUS_CONFIG = {
  active: { label: 'Active', badge: 'border-success/40 bg-success/10 text-success', icon: CheckCircle },
  suspended: { label: 'Suspended', badge: 'border-danger/40 bg-danger/10 text-danger', icon: XCircle },
  trial: { label: 'Trial', badge: 'border-warning/40 bg-warning/10 text-warning', icon: Clock }
};

const PLAN_CONFIG = {
  basic: { label: 'Basic', badge: 'bg-muted/60 text-muted-foreground border-border', features: ['Preset themes', '10 users', '1 domain'] },
  professional: { label: 'Professional', badge: 'bg-primary/10 text-primary border-primary/30', features: ['Color picker', '50 users', '3 domains'] },
  enterprise: { label: 'Enterprise', badge: 'bg-accent/15 text-accent border-accent/30', features: ['Custom CSS', 'Unlimited users', '10 domains'] }
};

function CreateTenantDialog({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    plan: 'basic',
    contact_email: ''
  });

  const createMutation = useMutation({
    mutationFn: (data) => axios.post(`${API_URL}/tenants/`, data, { headers: getAuthHeaders() }),
    onSuccess: () => {
      toast.success('Tenant created successfully');
      setOpen(false);
      setFormData({ name: '', slug: '', plan: 'basic', contact_email: '' });
      onSuccess();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create tenant')
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="tap rounded-full shadow-glow" data-testid="create-tenant-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Tenant
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Building2 className="h-5 w-5 text-primary" />
            Create New Tenant
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-foreground/90">Tenant Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  name: e.target.value,
                  slug: generateSlug(e.target.value)
                });
              }}
              placeholder="Acme Marketing Agency"
              className="bg-secondary/60 border-border"
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="text-foreground/90">Slug (URL identifier)</Label>
            <Input
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: generateSlug(e.target.value) })}
              placeholder="acme-marketing"
              className="bg-secondary/60 border-border font-mono"
              required
            />
            <p className="text-xs text-muted-foreground break-all">Used in URLs: {formData.slug || 'tenant-slug'}.yourplatform.com</p>
          </div>
          <div className="space-y-2">
            <Label className="text-foreground/90">Plan</Label>
            <Select value={formData.plan} onValueChange={(v) => setFormData({ ...formData, plan: v })}>
              <SelectTrigger className="bg-secondary/60 border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PLAN_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <span>{config.label}</span>
                      <span className="text-xs text-muted-foreground">({config.features[0]})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-foreground/90">Contact Email</Label>
            <Input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              placeholder="owner@acme.com"
              className="bg-secondary/60 border-border"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="ghost" className="tap" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" className="tap" disabled={createMutation.isPending}>
              {createMutation.isPending ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Tenant
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TenantCard({ tenant, onUpdate, onDelete }) {
  const statusConfig = STATUS_CONFIG[tenant.status] || STATUS_CONFIG.active;
  const planConfig = PLAN_CONFIG[tenant.plan] || PLAN_CONFIG.basic;
  const StatusIcon = statusConfig.icon;
  const branding = tenant.branding || {};
  const primaryDomain = tenant.tenant_domains?.find(d => d.is_primary)?.domain
    || tenant.tenant_domains?.[0]?.domain;

  const updateStatusMutation = useMutation({
    mutationFn: (status) => axios.put(
      `${API_URL}/tenants/${tenant.id}`,
      { status },
      { headers: getAuthHeaders() }
    ),
    onSuccess: () => {
      toast.success('Tenant status updated');
      onUpdate();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update tenant')
  });

  const deleteMutation = useMutation({
    mutationFn: () => axios.delete(`${API_URL}/tenants/${tenant.id}`, { headers: getAuthHeaders() }),
    onSuccess: () => {
      toast.success('Tenant deleted');
      onDelete();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to delete tenant')
  });

  return (
    <Card className="aurora-card min-w-0 transition-colors hover:border-primary/40" data-testid={`tenant-card-${tenant.slug}`}>
      <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt="" className="h-8 w-8 object-contain" />
              ) : (
                <Building2 className="h-6 w-6" />
              )}
            </div>
            <div className="min-w-0">
              <CardTitle className="truncate font-heading text-base font-semibold text-foreground">{tenant.name}</CardTitle>
              <code className="block truncate text-xs text-muted-foreground font-mono">{tenant.slug}</code>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Badge variant="outline" className={cn('rounded-full', statusConfig.badge)}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {statusConfig.label}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="tap text-muted-foreground hover:text-foreground" aria-label={`Actions for ${tenant.name}`}>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {tenant.status !== 'active' && (
                  <DropdownMenuItem onClick={() => updateStatusMutation.mutate('active')} className="text-success">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Activate
                  </DropdownMenuItem>
                )}
                {tenant.status !== 'suspended' && (
                  <DropdownMenuItem onClick={() => updateStatusMutation.mutate('suspended')} className="text-warning">
                    <XCircle className="h-4 w-4 mr-2" />
                    Suspend
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => deleteMutation.mutate()} className="text-danger">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 p-5 pt-0 sm:p-6 sm:pt-0">
        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
          <div className="min-w-0 rounded-xl bg-secondary/60 p-3">
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Plan</p>
            <Badge className={cn('rounded-full', planConfig.badge)}>
              {planConfig.label}
            </Badge>
          </div>
          <div className="min-w-0 rounded-xl bg-secondary/60 p-3">
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Users</p>
            <div className="flex items-center gap-1.5">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold text-foreground tnum">{tenant.user_count || 0}</span>
            </div>
          </div>
          <div className="min-w-0 rounded-xl bg-secondary/60 p-3">
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Domains</p>
            <div className="flex items-center gap-1.5">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold text-foreground tnum">{tenant.tenant_domains?.length || 0}</span>
            </div>
          </div>
        </div>

        {primaryDomain && (
          <div className="flex items-center justify-between gap-2 rounded-xl bg-secondary/60 p-3">
            <div className="flex min-w-0 items-center gap-2">
              <Globe className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate text-sm text-foreground/90">{primaryDomain}</span>
            </div>
            <a
              href={`https://${primaryDomain}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${primaryDomain} in a new tab`}
              className="tap inline-flex shrink-0 items-center justify-center rounded-lg text-primary transition-colors hover:text-primary/80"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        )}

        {branding.app_name && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl bg-secondary/60 p-3 text-sm">
            <Palette className="h-4 w-4 shrink-0 text-accent" />
            <span className="text-muted-foreground">Branded as:</span>
            <span className="min-w-0 truncate text-foreground">{branding.app_name}</span>
            {branding.theme && (
              <Badge variant="outline" className="ml-auto rounded-full border-border text-xs text-muted-foreground">
                {branding.theme}
              </Badge>
            )}
          </div>
        )}

        {tenant.contact_email && (
          <p className="text-sm text-muted-foreground break-all">
            Contact: <span className="text-foreground/90">{tenant.contact_email}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/* Two placeholder tenant cards while the first list loads. */
function TenantsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2" aria-busy="true" aria-label="Loading tenants" data-testid="tenants-skeleton">
      {[0, 1].map((i) => (
        <div key={i} className="aurora-card p-5 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 shrink-0 animate-pulse rounded-xl bg-secondary/60" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-4 w-2/5 animate-pulse rounded-md bg-secondary/60" />
              <div className="h-3 w-1/4 animate-pulse rounded-md bg-secondary/60" />
            </div>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[0, 1, 2].map((j) => <div key={j} className="h-16 animate-pulse rounded-xl bg-secondary/60" />)}
          </div>
          <div className="mt-3 h-11 animate-pulse rounded-xl bg-secondary/60" />
        </div>
      ))}
    </div>
  );
}

function TenantsPageInner() {
  usePageTitle('Tenants');
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tenants', statusFilter],
    queryFn: () => axios.get(`${API_URL}/tenants/`, {
      headers: getAuthHeaders(),
      params: statusFilter !== 'all' ? { status: statusFilter } : {}
    }).then(res => res.data)
  });

  const tenants = data?.tenants || [];

  if (error) {
    return (
      <div className="space-y-6" data-testid="tenants-page">
        <PageHeader
          icon={Building2}
          title="Tenant Management"
          description="Manage white-label resellers and their branding"
        />
        <div className="aurora-card p-8 text-center sm:p-10" role="alert" data-testid="tenants-error">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-danger/10 text-danger ring-1 ring-danger/20">
            <AlertCircle className="h-6 w-6" />
          </span>
          <h3 className="mt-4">Failed to load tenants</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty break-words">{error.response?.data?.detail || error.message}</p>
          <Button variant="outline" onClick={() => refetch()} className="tap mt-5 rounded-full">
            <RefreshCw className="h-4 w-4" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenants-page">
      <PageHeader
        icon={Building2}
        title="Tenant Management"
        description="Manage white-label resellers and their branding"
        actions={<CreateTenantDialog onSuccess={() => refetch()} />}
      />

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter tenants by status" data-testid="tenant-status-filter">
        <Button
          variant={statusFilter === 'all' ? 'default' : 'ghost'}
          size="sm"
          className={cn('tap rounded-full', statusFilter !== 'all' && 'text-muted-foreground hover:text-foreground')}
          aria-pressed={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
          data-testid="tenant-filter-all"
        >
          All
        </Button>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <Button
            key={key}
            variant={statusFilter === key ? 'default' : 'ghost'}
            size="sm"
            className={cn('tap rounded-full', statusFilter !== key && 'text-muted-foreground hover:text-foreground')}
            aria-pressed={statusFilter === key}
            onClick={() => setStatusFilter(key)}
            data-testid={`tenant-filter-${key}`}
          >
            {config.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <TenantsSkeleton />
      ) : tenants.length === 0 ? (
        <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10" data-testid="tenants-empty">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
            <Building2 className="h-6 w-6" />
          </span>
          <h3 className="mt-4">No tenants found</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">Create your first white-label tenant to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {tenants.map(tenant => (
            <TenantCard
              key={tenant.id}
              tenant={tenant}
              onUpdate={() => refetch()}
              onDelete={() => queryClient.invalidateQueries(['tenants'])}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The backend for this page does not exist on the Spring server yet (it still calls V2's API).
 * The banner is rendered ABOVE the original page on every path - loading, error, empty - so the
 * state is never mistaken for "no data". Remove the wrapper when the /uiapi endpoints land.
 */
export default function TenantsPage() {
  return (
    <>
      <NotWiredBanner what="Tenant management" legacyUrl={null} legacyLabel={null} />
      <TenantsPageInner />
    </>
  );
}
